import { useNavigate } from "react-router-dom"
import AppShell from "@/components/AppShell/AppShell"
import Button from "@/components/Button/Button"
import DataTable from "@/components/DataTable/DataTable"
import type { Column } from "@/components/DataTable/DataTable"
import StatusDot from "@/components/StatusDot/StatusDot"
import StatusScreen from "@/components/StatusScreen/StatusScreen"
import { UNTITLED_SESSION } from "@/content/customers"
import {
  REPORTS_BLURB,
  REPORTS_EMPTY,
  REPORTS_TITLE,
  TABLE_CUSTOMER,
  TABLE_DATE,
  TABLE_REPORT,
  TABLE_SESSION,
  TABLE_STATUS,
} from "@/content/nav"
import type { FacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import { reportPath } from "@/utils/routePaths"
import { shortDate } from "@/utils/relativeTime"
import { sessionState } from "@/utils/sessionLifecycle"
import type { Session } from "@/types"
import styles from "@/styles/pages/IndexPage.module.css"

/**
 * One debrief per finished session.
 *
 * The same query as the sessions index, filtered to `complete` here rather than
 * server-side — Firestore judges a `list` rule against the query's potential
 * result set, so the only scope the rules permit is `array-contains
 * facilitatorIds`, and anything narrower is a client-side filter afterwards.
 *
 * `complete` comes from `sessionState`, which folds expiry in: a session that
 * ran out never finished, so it has no debrief to read.
 */
export default function ReportsList() {
  return <AppShell>{(index) => <ReportsScreen index={index} />}</AppShell>
}

function ReportsScreen({ index }: { index: FacilitatorIndex }) {
  const navigate = useNavigate()
  const { sessions, error, loading, reload } = index

  if (error !== null && sessions === null) {
    return (
      <StatusScreen
        nested
        eyebrow={REPORTS_TITLE}
        title="The report list did not load."
        raw={error}
      >
        <Button loading={loading} onClick={reload}>
          Try again
        </Button>
      </StatusScreen>
    )
  }

  if (sessions === null) {
    return <StatusScreen nested eyebrow={REPORTS_TITLE} title="Loading reports." waiting />
  }

  const finished = sessions.filter((session) => sessionState(session) === "complete")

  const columns: readonly Column<Session>[] = [
    {
      key: "customer",
      header: TABLE_CUSTOMER,
      width: "28%",
      render: (session) => (
        <span className={styles.strong}>{session.customerName || "Untitled customer"}</span>
      ),
    },
    {
      key: "session",
      header: TABLE_SESSION,
      width: "28%",
      render: (session) => session.name || UNTITLED_SESSION,
    },
    {
      key: "date",
      header: TABLE_DATE,
      width: "18%",
      render: (session) => shortDate(session.completedAt ?? session.updatedAt),
    },
    {
      key: "status",
      header: TABLE_STATUS,
      width: "14%",
      render: (session) => <StatusDot state={sessionState(session)} compact />,
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (session) => (
        <button
          type="button"
          className={styles.link}
          onClick={() => navigate(reportPath(session.id))}
        >
          {TABLE_REPORT}
        </button>
      ),
    },
  ]

  return (
    <>
      <header className={styles.head}>
        <h1 className={styles.title}>{REPORTS_TITLE}</h1>
        <p className={styles.blurb}>{REPORTS_BLURB}</p>
      </header>

      <DataTable
        caption={REPORTS_BLURB}
        columns={columns}
        rows={finished}
        rowKey={(session) => session.id}
        empty={<div className={styles.empty}>{REPORTS_EMPTY}</div>}
      />
    </>
  )
}
